import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import type { DealItem, DealItemWriteRequest } from "src/dm/interfaces/DealItem";

/**
 * Bulk-edit-items hook — drives the rebuilt SPA `BulkEditItems` page
 * (replaces legacy `Item/BulkEditItems.aspx`). The legacy ASP.NET page
 * accepted a batch of pricing fields + item attributes + shipper dates,
 * looped them across every item selected on `ViewDealItems.aspx`, and
 * branched between an "all zones" insert and a "specific" filter that
 * scoped each child-row write to a chosen zone/override/store/promo
 * group.
 *
 * The SPA fans out to the existing per-item endpoints under
 * `DealItemSubController` (allowances / scans / promo-prices) and
 * `DealItemsController` (PUT for projected units / UOM / exclude flags /
 * shipper dates). No new write endpoints required — the per-item POSTs
 * already enforce the DSD-only Off-Invoice rule, vendor-scanback
 * divisibility, and the trigger-suppression bracket.
 *
 * Chunking at 950 (legacy MAXELEMENTS) is preserved here on the client
 * because each per-item POST runs its own margin-recalc; bigger batches
 * risk timing out the proxy.
 */

interface AllowanceFields {
  costZoneNumber: number;
  offInvoiceAmount?: number | null;
  billBackAmount?: number | null;
  countRecountAmount?: number | null;
  passThruAmount?: number | null;
  reflectAmount?: number | null;
}

interface VendorScanFields {
  zoneNumber: number;
  amount: number;
  /** A=Advantage Card, T=Total. */
  movementCode?: string | null;
  businessUnit?: string | null;
  supportTeamMember?: string | null;
  apVendorNumber?: string | null;
  addressSequence?: number | null;
  entryType?: string | null;
  invoiceDescription?: string | null;
  /** Sentinel for vendor scans — "" or "vendor" written by the server. */
  internalVendorCode?: string | null;
}

interface InternalScanFields {
  zoneNumber: number;
  amount: number;
  /** A=Advantage Card, T=Total. */
  movementCode: string;
  internalVendorCode: string;
}

interface PromoPriceFields {
  zoneNumber: number;
  multiUnitCount?: number | null;
  price?: number | null;
  amountOff?: number | null;
  percentOff?: number | null;
  specialPricing?: string | null;
  useCurrentPrice?: boolean;
}

interface ItemAttributeFields {
  projectedUnits?: number | null;
  projectedUnitsUom?: string | null;
  /** Tri-state — "Yes" / "No" / undefined for "leave alone". */
  excludeAdPlan?: boolean;
  excludeStoreAdPlan?: boolean;
}

interface ShipperDateFields {
  shipperFirstOrderDate?: string | null;
  shipperLastOrderDate?: string | null;
  shipperFirstShipDate?: string | null;
  shipperLastShipDate?: string | null;
}

/**
 * Apply-scope filter — when "specific", each per-item child-row write is
 * scoped to the matching zone / override / store / promo group only.
 * "All" applies to a single zone-0 row per child type (legacy default).
 */
export interface ApplyScope {
  mode: "all" | "specific";
  zones?: number[];
  overrides?: { zoneNumber: number; overrideNumber: number }[];
  stores?: number[];
  promoGroups?: number[];
}

export interface BulkEditPayload {
  dealItemIds: number[];
  allowance?: AllowanceFields | null;
  vendorScan?: VendorScanFields | null;
  internalScan?: InternalScanFields | null;
  promoPrice?: PromoPriceFields | null;
  itemAttributes?: ItemAttributeFields | null;
  shipperDates?: ShipperDateFields | null;
  applyScope?: ApplyScope;
}

export interface BulkEditResult {
  total: number;
  succeeded: number;
  failed: number;
  errors: Array<{ dealItemId: number; section: string; message: string }>;
}

/** Legacy parity. 950 = MAXELEMENTS in `BulkEditItems.aspx.cs`. */
const CHUNK = 950;

const sectionUrl = (dealId: number, dealItemId: number, section: string) =>
  `${APIS.Deals}/${dealId}/items/${dealItemId}/${section}`;

const itemUrl = (dealId: number, dealItemId: number) =>
  `${APIS.Deals}/${dealId}/items/${dealItemId}`;

const tryPost = async <T,>(
  url: string,
  body: T,
  errors: BulkEditResult["errors"],
  dealItemId: number,
  section: string
): Promise<boolean> => {
  try {
    await apiClient.post(url, body);
    return true;
  } catch (err) {
    const message =
      (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ??
      (err instanceof Error ? err.message : "Request failed");
    errors.push({ dealItemId, section, message });
    return false;
  }
};

const tryPut = async <T,>(
  url: string,
  body: T,
  errors: BulkEditResult["errors"],
  dealItemId: number,
  section: string
): Promise<boolean> => {
  try {
    await apiClient.put(url, body);
    return true;
  } catch (err) {
    const message =
      (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ??
      (err instanceof Error ? err.message : "Request failed");
    errors.push({ dealItemId, section, message });
    return false;
  }
};

/**
 * Build the list of {zoneNumber, overrideNumber, storeNumber, promoGroupId}
 * tuples that one child-row write should expand into. Legacy parity:
 * "All" yields a single row with all four nulls/zero; "Specific" yields
 * the cartesian product of the picked filters (with nulls where the user
 * didn't pick any of that category, so the absence of a filter doesn't
 * collapse the result set to zero).
 */
const expandScope = (
  scope: ApplyScope | undefined
): Array<{
  zoneNumber: number;
  overrideNumber: number | null;
  storeNumber: number | null;
  promoGroupId: number | null;
}> => {
  if (!scope || scope.mode === "all") {
    return [
      {
        zoneNumber: 0,
        overrideNumber: null,
        storeNumber: null,
        promoGroupId: null,
      },
    ];
  }
  const zones = scope.zones?.length ? scope.zones : [0];
  const overrides = scope.overrides?.length ? scope.overrides : null;
  const stores = scope.stores?.length ? scope.stores : [null];
  const groups = scope.promoGroups?.length ? scope.promoGroups : [null];
  const out: Array<{
    zoneNumber: number;
    overrideNumber: number | null;
    storeNumber: number | null;
    promoGroupId: number | null;
  }> = [];
  // Overrides override the zones array (zone+override come as a pair).
  // If both zones and overrides are populated, we use overrides for
  // (zone, override) pairs and let stores/groups expand orthogonally.
  if (overrides) {
    for (const ov of overrides) {
      for (const st of stores) {
        for (const pg of groups) {
          out.push({
            zoneNumber: ov.zoneNumber,
            overrideNumber: ov.overrideNumber,
            storeNumber: st,
            promoGroupId: pg,
          });
        }
      }
    }
  } else {
    for (const z of zones) {
      for (const st of stores) {
        for (const pg of groups) {
          out.push({
            zoneNumber: z,
            overrideNumber: null,
            storeNumber: st,
            promoGroupId: pg,
          });
        }
      }
    }
  }
  return out;
};

export const useBulkEditItems = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: BulkEditPayload & { itemLookup?: Map<number, DealItem> }
    ): Promise<BulkEditResult> => {
      const ids = payload.dealItemIds;
      const result: BulkEditResult = {
        total: ids.length,
        succeeded: 0,
        failed: 0,
        errors: [],
      };
      if (ids.length === 0) return result;
      const scopeRows = expandScope(payload.applyScope);
      const itemLookup = payload.itemLookup ?? new Map<number, DealItem>();

      for (let offset = 0; offset < ids.length; offset += CHUNK) {
        const slice = ids.slice(offset, offset + CHUNK);
        // Per-chunk concurrency is intentionally serial: each per-item
        // POST triggers a server-side `RecalcMarginsAsync(dealId)` (see
        // DealItemSubController). Running them all in parallel against
        // the same deal serializes on the Oracle margin proc anyway,
        // and the failure log stays readable in id order.
        for (const dealItemId of slice) {
          let allSectionsOk = true;

          // 1. Allowance — one row per scope tuple.
          if (payload.allowance) {
            for (const row of scopeRows) {
              const body = {
                ...payload.allowance,
                costZoneNumber: row.zoneNumber || payload.allowance.costZoneNumber || 0,
                storeNumber: row.storeNumber ?? undefined,
              };
              const ok = await tryPost(
                sectionUrl(dealId, dealItemId, "allowances"),
                body,
                result.errors,
                dealItemId,
                "allowance"
              );
              if (!ok) allSectionsOk = false;
            }
          }

          // 2. Vendor scanback — one row per scope tuple.
          if (payload.vendorScan) {
            for (const row of scopeRows) {
              const body = {
                ...payload.vendorScan,
                zoneNumber: row.zoneNumber || payload.vendorScan.zoneNumber || 0,
                overrideNumber: row.overrideNumber ?? undefined,
                storeNumber: row.storeNumber ?? undefined,
                promoGroupId: row.promoGroupId ?? undefined,
                internalVendorCode: payload.vendorScan.internalVendorCode ?? "",
                movementCode: payload.vendorScan.movementCode ?? "",
              };
              const ok = await tryPost(
                sectionUrl(dealId, dealItemId, "scans"),
                body,
                result.errors,
                dealItemId,
                "vendorScan"
              );
              if (!ok) allSectionsOk = false;
            }
          }

          // 3. Internal scanback — one row per scope tuple.
          if (payload.internalScan) {
            for (const row of scopeRows) {
              const body = {
                ...payload.internalScan,
                zoneNumber: row.zoneNumber || payload.internalScan.zoneNumber || 0,
                overrideNumber: row.overrideNumber ?? undefined,
                storeNumber: row.storeNumber ?? undefined,
                promoGroupId: row.promoGroupId ?? undefined,
              };
              const ok = await tryPost(
                sectionUrl(dealId, dealItemId, "scans"),
                body,
                result.errors,
                dealItemId,
                "internalScan"
              );
              if (!ok) allSectionsOk = false;
            }
          }

          // 4. Promo price — one row per scope tuple.
          if (payload.promoPrice) {
            for (const row of scopeRows) {
              const body = {
                ...payload.promoPrice,
                zoneNumber: row.zoneNumber || payload.promoPrice.zoneNumber || 0,
                overrideNumber: row.overrideNumber ?? undefined,
                storeNumber: row.storeNumber ?? undefined,
                promoGroupId: row.promoGroupId ?? undefined,
              };
              const ok = await tryPost(
                sectionUrl(dealId, dealItemId, "promo-prices"),
                body,
                result.errors,
                dealItemId,
                "promoPrice"
              );
              if (!ok) allSectionsOk = false;
            }
          }

          // 5. Item attributes + shipper dates — single PUT on the deal
          //    item. The PUT replaces the row, so we only fire it when
          //    something actually changed; we read the existing row from
          //    `itemLookup` so we can preserve everything we don't touch.
          if (payload.itemAttributes || payload.shipperDates) {
            const existing = itemLookup.get(dealItemId);
            if (existing) {
              const merged: DealItemWriteRequest = {
                retailItemNumber: existing.retailItemNumber,
                priceGroupCode: existing.priceGroupCode,
                supplierNumber: existing.supplierNumber,
                familyCode: existing.familyCode,
                projectedUnits:
                  payload.itemAttributes?.projectedUnits !== undefined
                    ? payload.itemAttributes.projectedUnits
                    : existing.projectedUnits,
                projectedUnitsUom:
                  payload.itemAttributes?.projectedUnitsUom !== undefined
                    ? payload.itemAttributes.projectedUnitsUom
                    : existing.projectedUnitsUom,
                warehouseFacilityNumber: existing.warehouseFacilityNumber,
                rewardItemTypeCode: existing.rewardItemTypeCode,
                shipperFirstOrderDate:
                  payload.shipperDates?.shipperFirstOrderDate ??
                  existing.shipperFirstOrderDate,
                shipperLastOrderDate:
                  payload.shipperDates?.shipperLastOrderDate ??
                  existing.shipperLastOrderDate,
                shipperFirstShipDate:
                  payload.shipperDates?.shipperFirstShipDate ??
                  existing.shipperFirstShipDate,
                shipperLastShipDate:
                  payload.shipperDates?.shipperLastShipDate ??
                  existing.shipperLastShipDate,
                excludeAdPlan:
                  payload.itemAttributes?.excludeAdPlan !== undefined
                    ? payload.itemAttributes.excludeAdPlan
                    : existing.excludeAdPlan,
                excludeStoreAdPlan:
                  payload.itemAttributes?.excludeStoreAdPlan !== undefined
                    ? payload.itemAttributes.excludeStoreAdPlan
                    : existing.excludeStoreAdPlan,
                addedByFamily: existing.addedByFamily,
                eligibleForSecondary: existing.eligibleForSecondary,
              };
              const ok = await tryPut(
                itemUrl(dealId, dealItemId),
                merged,
                result.errors,
                dealItemId,
                "itemAttributes"
              );
              if (!ok) allSectionsOk = false;
            } else {
              result.errors.push({
                dealItemId,
                section: "itemAttributes",
                message:
                  "Existing item not found in cache; refresh the items grid and retry.",
              });
              allSectionsOk = false;
            }
          }

          if (allSectionsOk) result.succeeded += 1;
          else result.failed += 1;
        }
      }
      return result;
    },
    onSuccess: () => {
      // Caches that depend on item allowances / scans / promo-prices /
      // margins all live under these query keys.
      qc.invalidateQueries({ queryKey: ["deal-items", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};
